import React, { useState, useEffect, useRef } from 'react';
import { ArrowUpDownIcon, RefreshCwIcon, InfoIcon } from 'lucide-react';
import TokenSelectorV2 from './TokenSelectorV2';
import SlippageSettings from './SlippageSettings';
import SwapButton from './SwapButton';
import { TOKENS, DEFAULT_SLIPPAGE } from '../config/constantsV2';
import { formatTokenAmount, debounce } from '../utils/helpers';
import { useSwapQuoteV2 } from '../hooks/useSwapQuoteV2';
import { useTokenBalance } from '../hooks/useTokenBalance';

const SwapInterfaceV2 = ({ 
  account, 
  onConnect, 
  onSwap, 
  isSwapping 
}) => {
  const [fromToken, setFromToken] = useState(TOKENS.FTM);
  const [toToken, setToToken] = useState(TOKENS.LIF3_NEW);
  const [fromAmount, setFromAmount] = useState('');
  const [slippage, setSlippage] = useState(DEFAULT_SLIPPAGE);
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const [showRouteDetails, setShowRouteDetails] = useState(false);

  // Custom hooks
  const { balance: fromBalance, loading: balanceLoading } = useTokenBalance(fromToken, account);
  const { 
    quote: swapQuote, 
    loading: quoteLoading, 
    error: quoteError,
    fetchQuote 
  } = useSwapQuoteV2();

  // Track previous tokens to detect changes
  const prevTokensRef = useRef({ from: null, to: null });

  // Fetch quote when inputs change
  useEffect(() => {
    if (fromToken && toToken && fromAmount && parseFloat(fromAmount) > 0) {
      // Check if tokens actually changed (not just amount)
      const tokenChanged = 
        prevTokensRef.current.from !== null &&
        (prevTokensRef.current.from?.address !== fromToken?.address ||
         prevTokensRef.current.to?.address !== toToken?.address);
      
      // Update previous tokens
      prevTokensRef.current = { from: fromToken, to: toToken };
      
      // Fetch immediately if token changed, otherwise use debounce
      fetchQuote(fromToken, toToken, fromAmount, tokenChanged);
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromToken, toToken, fromAmount]);

  // Auto-refresh quotes every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (fromToken && toToken && fromAmount && parseFloat(fromAmount) > 0) {
        fetchQuote(fromToken, toToken, fromAmount);
        setLastUpdateTime(Date.now());
      }
    }, 15000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromToken, toToken, fromAmount]);

  const handleSwapTokens = () => {
    const tempToken = fromToken;
    setFromToken(toToken);
    setToToken(tempToken);
    setFromAmount('');
  };

  const handleFromAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setFromAmount(value);
    }
  };

  const handleMaxAmount = () => {
    if (fromBalance && parseFloat(fromBalance) > 0) {
      const maxAmount = fromToken.isNative ? 
        Math.max(0, parseFloat(fromBalance) - 0.01).toString() : 
        fromBalance;
      setFromAmount(maxAmount);
    }
  };

  const handleRefreshQuote = () => {
    if (fromToken && toToken && fromAmount && parseFloat(fromAmount) > 0) {
      fetchQuote(fromToken, toToken, fromAmount);
      setLastUpdateTime(Date.now());
    }
  };

  const handleSwap = async () => {
    if (!swapQuote) return;

    await onSwap({
      fromToken,
      toToken,
      fromAmount,
      routeData: swapQuote,
      slippage,
    });

    // Clear amount after successful swap
    setFromAmount('');
  };

  const isSwapDisabled = 
    !account || 
    !fromAmount || 
    parseFloat(fromAmount) <= 0 || 
    !swapQuote || 
    quoteLoading || 
    quoteError ||
    (fromBalance && parseFloat(fromAmount) > parseFloat(fromBalance));

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6 shadow-2xl">
        {/* V2 Badge */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Swap</h2>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-bold rounded-full">
              V2 BETA
            </span>
            <button
              onClick={handleRefreshQuote}
              disabled={quoteLoading}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh quote"
            >
              <RefreshCwIcon className={`w-4 h-4 text-white/60 ${quoteLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* From Token */}
        <div className="space-y-2 mb-4">
          <TokenSelectorV2
            selectedToken={fromToken}
            onTokenSelect={setFromToken}
            excludeToken={toToken}
            label="From"
            disabled={isSwapping}
          />
          
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.0"
              value={fromAmount}
              onChange={handleFromAmountChange}
              disabled={isSwapping}
              className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white text-2xl font-semibold placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500/50 disabled:opacity-50"
            />
            
            {fromBalance && (
              <div className="flex items-center justify-between mt-2 px-2">
                <span className="text-sm text-white/60">
                  Balance: {parseFloat(fromBalance).toFixed(4)} {fromToken.symbol}
                </span>
                <button
                  onClick={handleMaxAmount}
                  disabled={isSwapping || balanceLoading}
                  className="text-sm text-blue-400 hover:text-blue-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  MAX
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Swap Direction Button */}
        <div className="flex justify-center -my-2 relative z-10">
          <button
            onClick={handleSwapTokens}
            disabled={isSwapping}
            className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowUpDownIcon className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* To Token */}
        <div className="space-y-2 mt-4 mb-4">
          <TokenSelectorV2
            selectedToken={toToken}
            onTokenSelect={setToToken}
            excludeToken={fromToken}
            label="To"
            disabled={isSwapping}
          />
          
          <div className="relative">
            <input
              type="text"
              placeholder="0.0"
              value={quoteLoading ? '' : (swapQuote?.estimatedOutputFormatted || '')}
              readOnly
              disabled
              className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white text-2xl font-semibold placeholder-white/30 opacity-70"
            />
            {quoteLoading && fromAmount && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <div className="flex items-center gap-2 text-blue-400">
                  <RefreshCwIcon className="w-4 h-4 animate-spin" />
                  <span className="text-xs opacity-60">Fetching quote...</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Route Information */}
        {swapQuote && (
          <div className="mb-4 p-4 bg-white/5 rounded-xl border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-white/60">Route via</span>
              <button
                onClick={() => setShowRouteDetails(!showRouteDetails)}
                className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
              >
                <InfoIcon className="w-4 h-4" />
                {showRouteDetails ? 'Hide' : 'Show'} Details
              </button>
            </div>
            
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-1 bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-300 text-xs font-semibold rounded">
                {swapQuote.dex.name}
              </span>
              <span className="px-2 py-1 bg-white/10 text-white/80 text-xs rounded">
                {swapQuote.type.toUpperCase()}
              </span>
              {swapQuote.directPath ? (
                <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs rounded">
                  Direct
                </span>
              ) : (
                <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 text-xs rounded">
                  Multi-hop
                </span>
              )}
            </div>

            {showRouteDetails && (
              <div className="space-y-2 pt-2 border-t border-white/10">
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Output (before fee)</span>
                  <span className="text-white font-medium">
                    {swapQuote.estimatedOutputFormatted} {toToken.symbol}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Fee (1%)</span>
                  <span className="text-white font-medium">
                    {swapQuote.feeAmountFormatted} {toToken.symbol}
                  </span>
                </div>
                {swapQuote.allQuotes && swapQuote.allQuotes.length > 1 && (
                  <div className="pt-2 border-t border-white/10">
                    <div className="text-xs text-white/60 mb-1">Other routes:</div>
                    {swapQuote.allQuotes.slice(0, 3).map((quote, idx) => (
                      <div key={idx} className="text-xs text-white/40 flex justify-between">
                        <span>{quote.dex} ({quote.type})</span>
                        <span>{quote.amountOut} {toToken.symbol}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {quoteError && fromAmount && parseFloat(fromAmount) > 0 && (
          <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
            <p className="text-red-400 text-sm">{quoteError}</p>
          </div>
        )}

        {/* Slippage Settings */}
        <div className="mb-4">
          <SlippageSettings
            slippage={slippage}
            onSlippageChange={setSlippage}
            disabled={isSwapping}
          />
        </div>

        {/* Swap Button */}
        <SwapButton
          isConnected={!!account}
          onConnect={onConnect}
          onSwap={handleSwap}
          isSwapping={isSwapping}
          canSwap={!isSwapDisabled}
          fromToken={fromToken}
          toToken={toToken}
          fromAmount={fromAmount}
          estimatedOutput={swapQuote?.estimatedOutputFormatted}
        />
      </div>

      {/* Info Box */}
      <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <div className="flex items-start gap-2">
          <InfoIcon className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-300">
            <p className="font-semibold mb-1">V2 Multi-DEX Router</p>
            <p className="text-blue-300/80">
              Automatically finds the best price across Lif3, SpookySwap, and WigoSwap (both V2 and V3). 
              You can also add custom tokens by address.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SwapInterfaceV2;
